import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { writeAuditLog } from '@/services/audit'
import type { Clue } from '@/types'

function cluesCol(incidentId: string) {
  return collection(db, 'incidents', incidentId, 'clues')
}

function clueDoc(incidentId: string, clueId: string) {
  return doc(db, 'incidents', incidentId, 'clues', clueId)
}

export const useCluesStore = defineStore('clues', () => {
  const clues = ref<Clue[]>([])

  let unsubscribe: Unsubscribe | null = null

  function subscribeToClues(incidentId: string) {
    if (unsubscribe) unsubscribe()
    const q = query(cluesCol(incidentId), orderBy('found_at', 'desc'))
    unsubscribe = onSnapshot(q, (snap) => {
      clues.value = snap.docs.map((d) => ({ clue_id: d.id, ...d.data() } as Clue))
    })
  }

  async function addClue(
    incidentId: string,
    data: Omit<Clue, 'clue_id' | 'incident_id' | 'status' | 'found_at'>,
  ): Promise<string> {
    const syncStore = useSyncStore()
    const payload = {
      ...data,
      incident_id: incidentId,
      status: 'unconfirmed' as const,
      found_at: serverTimestamp(),
    }
    syncStore.pendingWrites++
    try {
      const ref = await addDoc(cluesCol(incidentId), payload)
      await writeAuditLog(incidentId, 'clue.created', 'clue', ref.id, null, payload)
      return ref.id
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function updateClueStatus(
    incidentId: string,
    clueId: string,
    newStatus: Clue['status'],
  ): Promise<void> {
    const syncStore = useSyncStore()
    const existing = clues.value.find((c) => c.clue_id === clueId)
    const before = existing ? { status: existing.status } : null
    const after = { status: newStatus }

    syncStore.pendingWrites++
    try {
      await updateDoc(clueDoc(incidentId, clueId), after)
      await writeAuditLog(incidentId, 'clue.status_changed', 'clue', clueId, before, after)
    } finally {
      syncStore.pendingWrites--
    }
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    clues.value = []
  }

  return {
    clues,
    subscribeToClues,
    addClue,
    updateClueStatus,
    teardown,
  }
})
